import { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  white:  '#fff',
};

function calcDueDate(year, month, rentDueDay) {
  if (!rentDueDay || rentDueDay === 'end_of_month') {
    return new Date(year, month, 0).toISOString().split('T')[0];
  }
  const last = new Date(year, month, 0).getDate();
  const day  = Math.min(parseInt(rentDueDay) || 1, last);
  return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
}

function Sk({ w = '100%', h = 14, r = 8 }) {
  return <div className="skeleton" style={{ width: w, height: h, borderRadius: r }} />;
}

export default function Dashboard() {
  const { user, profile, subscription, refreshSubscription } = useAuth();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const upgradeSuccess = new URLSearchParams(search).get('upgrade') === 'success';
  const { isMobile } = useBreakpoint();

  const [loading, setLoading]   = useState(true);
  const [tenants, setTenants]   = useState([]);
  const [payments, setPayments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [marking, setMarking]   = useState({});
  const [sending, setSending]   = useState({});
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem('immohq_onboarding_dismissed') === '1'
  );

  useEffect(() => {
    fetchAll();
    if (upgradeSuccess) refreshSubscription();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAll() {
    const now   = new Date();
    const year  = now.getFullYear();
    const month = now.getMonth() + 1;
    const start = `${year}-${String(month).padStart(2,'0')}-01`;
    const end   = new Date(year, month, 0).toISOString().split('T')[0];

    const [{ data: t }, { data: p }, { data: pay }, { data: rem }] = await Promise.all([
      supabase.from('tenants').select('id, first_name, last_name, monthly_rent, rent_due_day, property_id').eq('owner_id', user.id),
      supabase.from('properties').select('id, name').eq('owner_id', user.id),
      supabase.from('rent_payments').select('*').eq('owner_id', user.id).gte('due_date', start).lte('due_date', end),
      supabase.from('reminders').select('id, payment_id, level').eq('owner_id', user.id),
    ]);
    setTenants(t || []);
    setProperties(p || []);
    setPayments(pay || []);
    setReminders(rem || []);
    setLoading(false);
  }

  /* ── Actions ── */
  async function handleMarkPaid(payment, tenant) {
    if (!payment) {
      const now   = new Date();
      const due   = calcDueDate(now.getFullYear(), now.getMonth() + 1, tenant.rent_due_day);
      const { error } = await supabase.from('rent_payments').insert({
        owner_id: user.id, tenant_id: tenant.id, property_id: tenant.property_id,
        amount: tenant.monthly_rent, due_date: due,
        paid_date: now.toISOString().split('T')[0], status: 'paid',
      });
      if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    } else {
      setMarking(m => ({ ...m, [payment.id]: true }));
      const { error } = await supabase.from('rent_payments')
        .update({ status: 'paid', paid_date: new Date().toISOString().split('T')[0] })
        .eq('id', payment.id).eq('owner_id', user.id);
      setMarking(m => ({ ...m, [payment.id]: false }));
      if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    }
    await fetchAll();
    addToast('Als bezahlt markiert.', 'success');
  }

  function handleSendReminder(payment) {
    if (!payment) return;
    const tenant = tenants.find(t => t.id === payment.tenant_id);
    const name = tenant ? `${tenant.first_name} ${tenant.last_name}` : 'Mieter';
    const betrag = payment.amount_due ? `${Number(payment.amount_due).toLocaleString('de-DE')} €` : '–';
    const monat = new Date(payment.due_date).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    navigate('/nachrichten', {
      state: {
        prefill: {
          type: 'reminder',
          tenantId: tenant?.id,
          tenantName: name,
          tenantEmail: tenant?.email || '',
          betreff: 'Offene Miete',
          inhalt: `Hallo ${name},\n\nfür die Miete in Höhe von ${betrag} (${monat}) besteht aktuell ein offener Betrag.\nBitte überweise diesen zeitnah.\n\nMit freundlichen Grüßen`,
        },
      },
    });
  }

  /* ── Derived state ── */
  const paidCount    = payments.filter(p => p.status === 'paid').length;
  const totalCount   = payments.length;
  const overdueCount = payments.filter(p => p.status === 'overdue').length;

  const actionItems = payments
    .filter(p => p.status !== 'paid')
    .map(p => {
      const tenant = tenants.find(t => t.id === p.tenant_id);
      if (!tenant) return null;
      const daysOverdue = p.due_date
        ? Math.max(0, Math.floor((new Date() - new Date(p.due_date)) / 86400000))
        : 0;
      const sentLevels = reminders.filter(r => r.payment_id === p.id).map(r => r.level);
      const maxLevel   = sentLevels.length ? Math.max(...sentLevels) : 0;
      const nextLevel  = maxLevel < 3 ? maxLevel + 1 : null;
      const propName   = properties.find(pr => pr.id === tenant.property_id)?.name || null;
      return { payment: p, tenant, daysOverdue, nextLevel, propName };
    })
    .filter(Boolean)
    .sort((a, b) => b.daysOverdue - a.daysOverdue);

  const remindersDue = actionItems.filter(item => item.payment.status === 'overdue' && item.nextLevel !== null).length;
  const isNewUser    = !loading && tenants.length === 0 && !onboardingDismissed;
  const firstName    = profile?.first_name || profile?.full_name?.split(' ')[0] || '';
  const currentMonth = new Date().toLocaleDateString('de-DE', { month: 'long' });

  function dismissOnboarding() {
    localStorage.setItem('immohq_onboarding_dismissed', '1');
    setOnboardingDismissed(true);
  }

  /* ── Skeleton ── */
  if (loading) return (
    <div style={{ maxWidth: 960 }}>
      <Sk w={220} h={30} r={8} />
      <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 14 }}>
        {[0,1,2].map(i => <Sk key={i} h={110} r={16} />)}
      </div>
      <div style={{ marginTop: 24 }}>
        <Sk w={180} h={20} r={6} />
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0,1,2].map(i => <Sk key={i} h={80} r={14} />)}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 960, color: T.ink }}>

      {/* ── Upgrade banner ── */}
      {upgradeSuccess && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>
              Upgrade erfolgreich — Willkommen im {subscription?.plan === 'enterprise' ? 'Enterprise' : 'Professional'}-Plan.
            </span>
          </div>
          <Link to="/dashboard" replace style={{ fontSize: 20, color: '#9ca3af', textDecoration: 'none', lineHeight: 1 }}>×</Link>
        </div>
      )}

      {/* ── Onboarding ── */}
      {isNewUser && (
        <div style={{ background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 16, padding: isMobile ? '24px 20px' : '28px 32px', marginBottom: 28, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', position: 'relative' }}>
          <button onClick={dismissOnboarding} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: T.muted, fontSize: 18, lineHeight: 1, padding: 4 }}>×</button>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.ink, marginBottom: 6 }}>
            Willkommen bei ImmoHQ{firstName ? `, ${firstName}` : ''} 👋
          </div>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 24 }}>In 3 Schritten startklar — dauert unter 2 Minuten.</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 12 }}>
            {[
              { step: 1, title: 'Erstes Objekt anlegen', desc: 'Trage deine Immobilie ein.', cta: 'Objekt anlegen →', path: '/properties' },
              { step: 2, title: 'Mieter hinzufügen',     desc: 'Mit Mietbeginn und Miethöhe.', cta: 'Mieter anlegen →', path: '/tenants' },
              { step: 3, title: 'Zahlungen tracken',      desc: 'ImmoHQ übernimmt den Rest.', cta: 'Zu Zahlungen →', path: '/payments' },
            ].map(s => (
              <div key={s.step} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 16px' }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'white' }}>{s.step}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 12, lineHeight: 1.5 }}>{s.desc}</div>
                <button onClick={() => navigate(s.path)} style={{ background: 'none', border: 'none', fontSize: 12, fontWeight: 700, color: T.ink, cursor: 'pointer', padding: 0 }}>
                  {s.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: isMobile ? 24 : 30, fontWeight: 900, letterSpacing: '-0.8px', color: T.ink }}>
          Übersicht
        </h1>
        <div style={{ fontSize: 14, color: T.muted }}>
          {overdueCount > 0
            ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{overdueCount} {overdueCount === 1 ? 'Miete überfällig' : 'Mieten überfällig'} — Aktion erforderlich</span>
            : 'Alles im Griff — keine offenen Posten.'
          }
        </div>
      </div>

      {/* ── Trial Banner ── */}
      {subscription?.status === 'trialing' && subscription?.trial_ends_at && (() => {
        const daysLeft = Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / 86400000));
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            background: daysLeft <= 3 ? '#fff5f5' : '#fffbeb',
            border: `1.5px solid ${daysLeft <= 3 ? '#fecaca' : '#fde68a'}`,
            borderRadius: 12, padding: '14px 20px', marginBottom: 20, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: daysLeft <= 3 ? '#fee2e2' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: daysLeft <= 3 ? '#dc2626' : '#b45309' }}>{daysLeft}</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
                  {daysLeft === 0 ? 'Testphase abgelaufen' : `Noch ${daysLeft} ${daysLeft === 1 ? 'Tag' : 'Tage'} in der Testphase`}
                </div>
                <div style={{ fontSize: 12, color: T.muted }}>
                  {daysLeft === 0 ? 'Wähle jetzt einen Plan um weiterzumachen.' : 'Teste alle Funktionen kostenlos. Danach wird ein Abo benötigt.'}
                </div>
              </div>
            </div>
            <button onClick={() => navigate('/pricing')} style={{
              fontSize: 13, fontWeight: 700, padding: '8px 18px', borderRadius: 8,
              background: T.ink, color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              Plan wählen
            </button>
          </div>
        );
      })()}

      {/* ── 3 KPI cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 14, marginBottom: 32 }}>

        {/* Überfällige Mieten */}
        <div style={{
          background: overdueCount > 0 ? '#fff5f5' : 'white',
          border: `1.5px solid ${overdueCount > 0 ? '#fecaca' : T.border}`,
          borderRadius: 16, padding: '22px 24px',
          boxShadow: overdueCount > 0 ? '0 2px 12px rgba(220,38,38,0.08)' : '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: overdueCount > 0 ? '#dc2626' : T.muted, marginBottom: 10 }}>
            ÜBERFÄLLIGE MIETEN
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1.5px', color: overdueCount > 0 ? '#dc2626' : T.ink, lineHeight: 1, marginBottom: 6 }}>
            {overdueCount}
          </div>
          <div style={{ fontSize: 13, color: overdueCount > 0 ? '#ef4444' : T.muted }}>
            {overdueCount > 0 ? 'Sofortiger Handlungsbedarf' : `Kein Rückstand im ${currentMonth}`}
          </div>
        </div>

        {/* Mahnungen ausstehend */}
        <div style={{
          background: remindersDue > 0 ? '#fffbeb' : 'white',
          border: `1.5px solid ${remindersDue > 0 ? '#fde68a' : T.border}`,
          borderRadius: 16, padding: '22px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: remindersDue > 0 ? '#b45309' : T.muted, marginBottom: 10 }}>
            MAHNUNGEN AUSSTEHEND
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1.5px', color: remindersDue > 0 ? '#b45309' : T.ink, lineHeight: 1, marginBottom: 6 }}>
            {remindersDue}
          </div>
          <div style={{ fontSize: 13, color: remindersDue > 0 ? '#d97706' : T.muted }}>
            {remindersDue > 0 ? 'Mahnung noch nicht versendet' : 'Alle Mahnungen verschickt'}
          </div>
        </div>

        {/* Zahlungen eingegangen */}
        <div style={{
          background: 'white', border: `1.5px solid ${T.border}`,
          borderRadius: 16, padding: '22px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: T.muted, marginBottom: 10 }}>
            ZAHLUNGEN {currentMonth.toUpperCase()}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, lineHeight: 1, marginBottom: 6 }}>
            <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1.5px', color: T.ink }}>{paidCount}</span>
            <span style={{ fontSize: 20, fontWeight: 600, color: T.muted }}>/ {totalCount}</span>
          </div>
          <div style={{ fontSize: 13, color: T.muted }}>
            {totalCount === 0 ? 'Noch keine Zahlungen' : paidCount === totalCount ? 'Alle eingegangen' : `${totalCount - paidCount} noch offen`}
          </div>
        </div>
      </div>

      {/* ── Aktionen heute ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.4px', color: T.ink }}>
            Aktionen heute
          </h2>
          <Link to="/tenants" style={{ fontSize: 13, fontWeight: 600, color: T.muted, textDecoration: 'none' }}>
            Alle Mieter →
          </Link>
        </div>

        {actionItems.length === 0 ? (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: 14, padding: '28px 28px',
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#dcfce7', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#15803d', marginBottom: 3 }}>Alles erledigt.</div>
              <div style={{ fontSize: 13, color: '#16a34a' }}>Aktuell keine offenen Posten — alle Mieten für diesen Monat sind eingegangen.</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {actionItems.map(({ payment, tenant, daysOverdue, nextLevel, propName }) => {
              const isOverdue  = payment.status === 'overdue';
              const isMarking_ = !!marking[payment?.id];
              const isSending_ = !!sending[payment?.id];

              return (
                <div key={payment?.id || tenant.id} style={{
                  background: 'white',
                  border: `1.5px solid ${isOverdue ? '#fecaca' : T.border}`,
                  borderLeft: `4px solid ${isOverdue ? '#ef4444' : '#f59e0b'}`,
                  borderRadius: 14, padding: isMobile ? '16px' : '18px 22px',
                  display: 'flex', alignItems: 'center', gap: 20,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  boxShadow: isOverdue ? '0 2px 10px rgba(220,38,38,0.06)' : '0 1px 4px rgba(0,0,0,0.04)',
                }}>

                  {/* Tenant info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
                        {tenant.first_name} {tenant.last_name}
                      </span>
                      {isOverdue ? (
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: '#dc2626',
                          background: '#fee2e2', borderRadius: 100, padding: '2px 8px',
                        }}>
                          {daysOverdue > 0 ? `${daysOverdue} Tage überfällig` : 'Überfällig'}
                        </span>
                      ) : (
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: '#92400e',
                          background: '#fef3c7', borderRadius: 100, padding: '2px 8px',
                        }}>
                          Ausstehend
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: T.muted }}>
                      {propName && <span>{propName} · </span>}
                      {tenant.monthly_rent
                        ? <span style={{ fontWeight: 600, color: T.ink }}>{Number(tenant.monthly_rent).toLocaleString('de-DE')} €</span>
                        : null
                      }
                      {payment?.due_date && (
                        <span> · Fällig: {new Date(payment.due_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 'auto' }}>
                      <button
                        onClick={() => handleSendReminder(payment)}
                        style={{
                          flex: isMobile ? 1 : 'none',
                          fontSize: 13, fontWeight: 600, height: 38, padding: '0 16px',
                          background: '#fffbeb', border: '1.5px solid #fde68a',
                          borderRadius: 8, color: '#b45309',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                          transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
                      >
                        Mahnung senden
                      </button>
                    <button
                      onClick={() => handleMarkPaid(payment, tenant)}
                      disabled={isMarking_}
                      style={{
                        flex: isMobile ? 1 : 'none',
                        fontSize: 13, fontWeight: 700, height: 38, padding: '0 16px',
                        background: T.ink, border: 'none',
                        borderRadius: 8, color: 'white',
                        cursor: isMarking_ ? 'wait' : 'pointer',
                        opacity: isMarking_ ? 0.6 : 1, whiteSpace: 'nowrap',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => { if (!isMarking_) e.currentTarget.style.background = '#333'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = T.ink; }}
                    >
                      {isMarking_ ? '…' : 'Als bezahlt markieren'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


    </div>
  );
}
